// Updated Data.tsx with three tables and corrections
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Modal,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomButton,
  CustomTable,
  HtmlEditor,
} from "../../../../global/components";
import CustomSelect from "../../../../global/components/CustomSelect/CustomSelect";
import strings from "../../../../global/constants/StringConstants";
import { handleErrorMessage, isTruthy } from "../../../../helpers/methods";
import { selectLanguage, selectProfileId } from "../../../../redux/authSlice";
import { useAppSelector } from "../../../../utils/hooks";
import {
  getAnalysisData,
  getIndividualPlanetData,
} from "../../AnalysisService";
import reportStyles from "./Report.styles";
import {
  fetchDetails,
  getReportByProfileId,
  getShortReport,
} from "./ReportService";

// English and Polish names from API (analysisDetails)
type RahuKey = "d1Rahu" | "d1RahuBhava" | "d10Rahu" | "d10RahuBhava";
const RAHU_NAMES: Record<RahuKey, readonly string[]> = {
  d1Rahu: [
    "D1 Rahu/Ketu in House (Rashi)",
    "D1 RAHU/KETU W DOMU OSOBISTY (Rashi)",
  ],
  d1RahuBhava: [
    "D1 Rahu/Ketu in House (Bhava)",
    "D1 RAHU/KETU W DOMU OSOBISTY (Bhava)",
  ],
  d10Rahu: [
    "D10 Rahu/Ketu in House (Rashi)",
    "D10 RAHU/KETU W DOMU ZWIĄZANY Z PROFESJĄ (Rashi)",
  ],
  d10RahuBhava: [
    "D10 Rahu/Ketu in House (Bhava)",
    "D10 RAHU/KETU W DOMU ZWIĄZANY Z PROFESJĄ (Bhava)",
  ],
};

interface RahuDetail {
  description: string;
  name: string;
  symbol?: string;
  value: string[];
}

const tableHeaders = [
  { name: "Planet/Point", field: "name:" },
  { name: "Value", field: "value" },
];

const tableHeadersNew = [
  { name: "", field: "delete" },
  { name: "Planet", field: "point_name" },
  { name: "House", field: "house_number" },
  { name: "Nakshatra", field: "value" },
  { name: "Zodiac", field: "sign_name" },
  { name: "House Nakshatra", field: "house_nakshatra_name" },
];

const houseMenu = [
  {
    value: "d1",
    content: "D1",
  },
  {
    value: "d2",
    content: "D2",
  },
  {
    value: "d3",
    content: "D3",
  },
  {
    value: "d4",
    content: "D4",
  },
  {
    value: "d7",
    content: "D7",
  },
  {
    value: "d9",
    content: "D9",
  },
  {
    value: "d10",
    content: "D10",
  },
  {
    value: "d12",
    content: "D12",
  },
  {
    value: "d16",
    content: "D16",
  },
  {
    value: "d20",
    content: "D20",
  },
  {
    value: "d24",
    content: "D24",
  },
  {
    value: "d27",
    content: "D27",
  },
  {
    value: "d30",
    content: "D30",
  },
  {
    value: "d40",
    content: "D40",
  },
  {
    value: "d45",
    content: "D45",
  },
  {
    value: "d60",
    content: "D60",
  },
];

const selectorOptions = [
  { label: "Nakshatra", value: "nakshatra" },
  { label: "House Nakshatra", value: "house_nakshatra" },
  { label: "Zodiac", value: "zodiac" },
  { label: "House", value: "house" },
];

const tableHeadersAllOptions = [
  { name: "Divisional Chart", field: "selectedHouse" },
  { name: "Planet", field: "point_name" },
  { name: "Nakshatra", field: "nakshatra" },
  { name: "House Nakshatra", field: "house_nakshatra" },
  { name: "Zodiac", field: "zodiac" },
  { name: "House", field: "house" },
];

const Data = ({
  formFields,
  setIsLoading,
  data,
  fileName,
  subTabValue,
}: any) => {
  const classes = reportStyles;
  const { t, i18n } = useTranslation();
  const profileId = useAppSelector(selectProfileId);
  const language = useAppSelector(selectLanguage);

  const [keyType, setKeyType] = useState("planets");
  const [selectedHouse, setSelectedHouse] = useState("");
  const [tableData, setTableData] = useState<any[]>([]);
  const [selected, setSelected] = useState<any[]>([]);
  const [persistedSelectedData, setPersistedSelectedData] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState("");
  const [selectorModalOpen, setSelectorModalOpen] = useState(false);
  const [rowPendingSelection, setRowPendingSelection] = useState<any>(null);
  const [modalSelectedOptions, setModalSelectedOptions] = useState<string[]>(
    [],
  );
  const isDataTab = subTabValue === strings.DATA;

  // Rahu/Ketu options from getAnalysisData (when !isDataTab), extracted by name
  const [rahuDetails, setRahuDetails] = useState<
    Record<RahuKey, RahuDetail | null>
  >({
    d1Rahu: null,
    d1RahuBhava: null,
    d10Rahu: null,
    d10RahuBhava: null,
  });
  const [d1Rashi, setD1Rashi] = useState(true);
  const [d10Rashi, setD10Rashi] = useState(true);

  useEffect(() => {
    if (isTruthy(data)) {
      setTableData(Object.values(data));
    }
  }, [data]);

  useEffect(() => {
    if (isDataTab || !profileId) return;
    const fetchRahuOptions = async () => {
      try {
        setIsLoading?.(true);
        const lang = i18n.language.toLowerCase();
        const response = await getAnalysisData(profileId, lang);
        const details: any[] = response?.analysisDetails ?? [];
        const byName = (Object.keys(RAHU_NAMES) as RahuKey[]).reduce(
          (acc, key) => {
            const names = RAHU_NAMES[key];
            const match = details.find(
              (item: any) =>
                item?.name && names.includes(String(item.name).trim()),
            );
            acc[key] = match ?? null;
            return acc;
          },
          {
            d1Rahu: null,
            d1RahuBhava: null,
            d10Rahu: null,
            d10RahuBhava: null,
          } as Record<RahuKey, RahuDetail | null>,
        );
        setRahuDetails(byName);
      } catch (e) {
        handleErrorMessage(e);
      } finally {
        setIsLoading?.(false);
      }
    };
    fetchRahuOptions();
  }, [isDataTab, profileId, i18n.language]);

  const handleKeyChange = (e: any) => {
    const val = e.target.value;
    setKeyType(val);
    if (val === "other") {
      getDetails();
    } else {
      setTableData([]);
      setSelectedHouse("");
    }
  };

  const handleHouseChange = async (e: SelectChangeEvent<string>) => {
    const val = e.target.value;
    setSelectedHouse(val);
    try {
      setIsLoading?.(true);
      const language = i18n.language.toLowerCase();
      const response = await getIndividualPlanetData(profileId!, val, language);
      const tableValue = Object.values(response.rashiDetails || {}).map(
        (item: any, index: number) => ({
          ...item,
          value: item.nakshatra_key + "." + item.name,
          id: val + item.point_name + item.nakshatra_key + item.abs_pos + index,
          selectedHouse: "D" + val.slice(1),
          name: null,
          pdf_key: item.point_name,
        }),
      );
      setTableData(tableValue);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading?.(false);
    }
  };

  const geocodeAddress = (addressString: string) => {
    return new Promise((resolve, reject) => {
      if (
        !window.google ||
        !window.google.maps ||
        !window.google.maps.Geocoder
      ) {
        reject(new Error("Google Maps API not loaded"));
        return;
      }

      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ address: addressString }, (results, status) => {
        if (
          status === "OK" &&
          results &&
          results[0] &&
          results[0].geometry &&
          results[0].geometry.location
        ) {
          const location = results[0].geometry.location;
          resolve({
            lat: location.lat(),
            lng: location.lng(),
          });
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
  };

  const getDetails = async () => {
    try {
      setIsLoading && setIsLoading(true);
      const rawDate = formFields.birthDate.value;
      const rawTime = formFields.birthTime.value;

      const combined = `${rawDate} ${rawTime}`;

      const dateObj = new Date(combined);

      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      const hours = String(dateObj.getHours()).padStart(2, "0");
      const minutes = String(dateObj.getMinutes()).padStart(2, "0");
      const seconds = String(dateObj.getSeconds()).padStart(2, "0");

      const formattedDateTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      const coordinates: any = await geocodeAddress("Surat, Gujarat, India");
      const profileData = {
        first_person: {
          name:
            formFields.firstName.value.trim() +
            " " +
            formFields.lastName.value.trim(),
          birth_date: formattedDateTime,
          longitude: coordinates.lng,
          latitude: coordinates.lat,
          altitude: "0",
          blood_group: formFields.bloodGroup.value,
          timezone: "Asia/Kolkata",
          isVedic: "1",
        },
      };
      const response = await fetchDetails(profileData);

      // convertToTableData(response.result);
      convertToTableDataNew(response.result, "D1", "other");
      setIsLoading && setIsLoading(false);
    } catch (error: any) {
      setIsLoading && setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const getObjectKey = (selectedName: string) => {
    switch (selectedName) {
      case "nakshatra":
        return "value";
      case "zodiac":
        return "sign_name";
      case "house_nakshatra":
        return "house_nakshatra_name";
      case "house":
        return "house_number";
      default:
        return "value";
    }
  };

  const convertToTableDataNew = (
    response: any,
    house: string,
    name: string,
  ) => {
    const isOther = name === "other";

    // Filter the response to only include items where name === 'other'
    const filteredResponse = Object.keys(response)
      .map((key) => response[key])
      .filter((item) => item.pdf_type === "other");

    console.log(filteredResponse);

    const tableValue = filteredResponse
      .map((item, index) => {
        const id = isOther
          ? item.abs_pos + item.name + item.index + item.value
          : house + item.house_number + item.nakshatra_key;

        return {
          ...item,
          id,
          selectedHouse: "D" + house.slice(1),
          name: isOther ? null : item.point_name + name,
          pdf_key: isOther ? item.pdf_key : item.point_name,
          pdf_type: name,
          value: isOther ? item.value : item.nakshatra_key + "." + item.name,
          filteredValue: isOther ? item.value : item[getObjectKey(name)],
        };
      })
      .filter(Boolean);
    const newTableRows = [
      {
        id: "circular_chart",
        selectedHouse: "D1",
        "name:": "Circular Chart",
        pdf_key: "Circular Chart",
        pdf_type: "other",
        value: "",
        filteredValue: "",
      },
      {
        id: "chakra_chart",
        selectedHouse: "D1",
        "name:": "Chakra Chart",
        pdf_key: "Chakra Chart",
        pdf_type: "other",
        value: "",
        filteredValue: "",
      },
      {
        id: "element_chart",
        selectedHouse: "D1",
        "name:": "Element Chart",
        pdf_key: "Element Chart",
        pdf_type: "other",
        value: "",
        filteredValue: "",
      },
      {
        id: "dosha_chart",
        selectedHouse: "D1",
        "name:": "Dosha Chart",
        pdf_key: "Dosha Chart",
        pdf_type: "other",
        value: "",
        filteredValue: "",
      },
      {
        id: "missing_minerals",
        selectedHouse: "D1",
        "name:": "Missing Minerals",
        pdf_key: "Missing Minerals",
        pdf_type: "other",
        value: "",
        filteredValue: "",
      },
      {
        id: "house_lords_personalised_meanings",
        selectedHouse: "D1",
        "name:": "House Lords – Personalised Meanings",
        pdf_key: "House Lords – Personalised Meanings",
        pdf_type: "other",
        value: "",
        filteredValue: "",
      },
      {
        id: "D1_planetary_table",
        selectedHouse: "D1",
        "name:": "D1 Planetary Table",
        pdf_key: "D1 Planetary Table",
        pdf_type: "other",
        value: "",
        filteredValue: "",
      },
      {
        id: "D10_planetary_table",
        selectedHouse: "D1",
        "name:": "D10 Planetary Table",
        pdf_key: "D10 Planetary Table",
        pdf_type: "other",
        value: "",
        filteredValue: "",
      },
      {
        id: "planetary_aspects_analysis",
        selectedHouse: "D1",
        "name:": "Planetary Aspects Analysis",
        pdf_key: "Planetary Aspects Analysis",
        pdf_type: "other",
        value: "",
        filteredValue: "",
      },
      {
        id: "yogas_yogakaraka",
        selectedHouse: "D1",
        "name:": "Yogas Yogakaraka",
        pdf_key: "Yogas Yogakaraka",
        pdf_type: "other",
        value: "",
        filteredValue: "",
      },
      {
        id: "arudha_lagna_upapada_lagna",
        selectedHouse: "D1",
        "name:": "Arudha Lagna & Upapada Lagna",
        pdf_key: "Arudha Lagna & Upapada Lagna",
        pdf_type: "other",
        value: "",
        filteredValue: "",
      },
      {
        id: "D7_children_fertility_analysis",
        selectedHouse: "D1",
        "name:": "D7 Children Fertility Analysis",
        pdf_key: "D7 Children Fertility Analysis",
        pdf_type: "other",
        value: "",
        filteredValue: "",
      },
      {
        id: "maha_dasha",
        selectedHouse: "D1",
        "name:": "Maha Dasha",
        pdf_key: "Maha Dasha",
        pdf_type: "other",
        value: "",
        filteredValue: "",
      },
      {
        id: "significant_changes_during_upcoming_three_year",
        selectedHouse: "D1",
        "name:": "Significant Changes During Upcoming Three Year",
        pdf_key: "Significant Changes During Upcoming Three Year",
        pdf_type: "other",
        value: "",
        filteredValue: "",
      },
      {
        id: "darakaraka_vs_7th_house_compatibility_score",
        selectedHouse: "D1",
        "name:": "Darakaraka vs 7th-house Compatibility Score",
        pdf_key: "Darakaraka vs 7th-house Compatibility Score",
        pdf_type: "other",
        value: "",
        filteredValue: "",
      },
      {
        id: "planet_colors_gems_directions_analysis",
        selectedHouse: "D1",
        "name:": "Planet Colors Gems Directions Analysis",
        pdf_key: "Planet Colors Gems Directions Analysis",
        pdf_type: "other",
        value: "",
        filteredValue: "",
      },
      {
        id: "mantras_mudras_lunar_rituals_analysis",
        selectedHouse: "D1",
        "name:": "Mantras Mudras Lunar Rituals Analysis",
        pdf_key: "Mantras Mudras Lunar Rituals Analysis",
        pdf_type: "other",
        value: "",
        filteredValue: "",
      },
      {
        id: "planet_vs_house_meaning",
        selectedHouse: "D1",
        "name:": "Planet Vs House Meaning",
        pdf_key: "Planet Vs House Meaning",
        pdf_type: "other",
        value: "",
        filteredValue: "",
      },
    ];
    setTableData(tableValue);
    if (isOther) {
      setTableData((prev) => [...prev, ...newTableRows]);
      console.log("Table Data:", tableData);
    }
  };

  const setSelectedData = (event: any, row: any) => {
    const isChecked = event.target.checked;

    if (!isChecked) {
      // Remove row
      setSelected((prev) => prev.filter((val) => val !== row.id));
      setPersistedSelectedData((prev) =>
        prev.filter((item) => item.id !== row.id),
      );
      return;
    }

    if (keyType === "other") {
      // Directly add without selection
      setSelected((prev) => [...prev, row.id]);
      setPersistedSelectedData((prev) => [...prev, row]);
    } else {
      // Show modal for selection
      setRowPendingSelection(row);
      setModalSelectedOptions([]); // Reset modal selections
      setSelectorModalOpen(true);
    }
  };

  const deleteRow = (row: any) => {
    setPersistedSelectedData((prev) =>
      prev.filter((r) => !(r.id === row.id && r.pdf_type === row.pdf_type)),
    );
    // Only remove from selected if no more entries exist for this row.id
    setSelected((prev) => {
      const remainingEntries = persistedSelectedData.filter(
        (r) => r.id === row.id && r.pdf_type !== row.pdf_type,
      );
      if (remainingEntries.length === 0) {
        return prev.filter((id) => id !== row.id);
      }
      return prev;
    });
  };

  const handleModalOptionToggle = (option: string) => {
    setModalSelectedOptions((prev) =>
      prev.includes(option)
        ? prev.filter((opt) => opt !== option)
        : [...prev, option],
    );
  };

  const handleModalConfirm = () => {
    const row = rowPendingSelection;
    if (!row || modalSelectedOptions.length === 0) return;

    // For each selected option, add a new entry if not already present
    setPersistedSelectedData((prev) => {
      const newRows = modalSelectedOptions
        .filter(
          (key) =>
            !prev.some((item) => item.id === row.id && item.pdf_type === key),
        )
        .map((key) => ({
          ...row,
          pdf_type: key,
          filteredValue: row[getObjectKey(key)],
        }));
      return [...prev, ...newRows];
    });
    setSelected((prev) => (prev.includes(row.id) ? prev : [...prev, row.id]));
    setSelectorModalOpen(false);
    setRowPendingSelection(null);
    setModalSelectedOptions([]);
  };

  const fetchReportByProfileId = async (book?: number) => {
    try {
      setIsLoading && setIsLoading(true);
      let payload: any[] = [];

      persistedSelectedData.map((data: any) => {
        payload.push([
          data.selectedHouse,
          null,
          data.pdf_key,
          data.pdf_type,
          data.filteredValue,
        ]);
      });
      const response = await getReportByProfileId(
        profileId,
        payload,
        language,
        isDataTab,
        !isDataTab ? d1Rashi : undefined,
        !isDataTab ? d10Rashi : undefined,
        book,
      );
      setContent(await response.text());
      setIsOpen(true);
      setIsLoading && setIsLoading(false);
    } catch (error: any) {
      handleErrorMessage(error);
      setIsLoading && setIsLoading(false);
    }
  };

  const fetchShortReport = async () => {
    try {
      setIsLoading && setIsLoading(true);
      let payload: any[] = [];
      persistedSelectedData.map((data: any) => {
        payload.push([
          data.selectedHouse,
          null,
          data.pdf_key,
          data.pdf_type,
          data.filteredValue,
        ]);
      });
      const response = await getShortReport(
        profileId,
        payload,
        language,
        isDataTab,
        !isDataTab ? d1Rashi : undefined,
        !isDataTab ? d10Rashi : undefined,
      );
      setContent(await response.text());
      setIsOpen(true);
      setIsLoading && setIsLoading(false);
    } catch (error: any) {
      handleErrorMessage(error);
      setIsLoading && setIsLoading(false);
    }
  };

  const handleContentSave = (updatedContent: string) => {
    setContent(updatedContent);
    // You can add additional logic here to save the updated content
    console.log("Content updated:", updatedContent);
  };

  // Compute display rows for the first table
  const displayRows = tableData.map((row) => {
    // Find all selected options for this row
    const selectedOptions = persistedSelectedData.filter(
      (item) => item.id === row.id,
    );
    // Build a row with values only for selected options
    const result: any = {
      ...row,
      nakshatra: "",
      house_nakshatra: "",
      zodiac: "",
      house: "",
    };
    selectedOptions.forEach((opt) => {
      if (opt.pdf_type && result.hasOwnProperty(opt.pdf_type)) {
        result[opt.pdf_type] = opt.filteredValue;
      }
    });
    return result;
  });

  // Compute display rows for the second table (Selected - Planets)
  const selectedPlanetsRows = persistedSelectedData
    .filter((r) => r.pdf_type !== "other")
    .map((item) => {
      return {
        ...item,
        selectedHouse: item.selectedHouse || "",
        nakshatra: item.pdf_type === "nakshatra" ? item.filteredValue : "",
        house_nakshatra:
          item.pdf_type === "house_nakshatra" ? item.filteredValue : "",
        zodiac: item.pdf_type === "zodiac" ? item.filteredValue : "",
        house: item.pdf_type === "house" ? item.filteredValue : "",
      };
    });

  return (
    <>
      {isDataTab && (
        <>
          <Box px={4} mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <CustomSelect
                  label="Key"
                  id="key"
                  name="key"
                  value={keyType}
                  onChange={handleKeyChange}
                  menuItems={[
                    { value: "planets", content: "Planets" },
                    { value: "other", content: "Other" },
                  ]}
                />
              </Grid>

              {keyType === "planets" && (
                <Grid item xs={12} md={6}>
                  <CustomSelect
                    label="Divisional Chart"
                    id="house"
                    name="house"
                    value={selectedHouse}
                    onChange={handleHouseChange}
                    menuItems={houseMenu}
                  />
                </Grid>
              )}
            </Grid>
          </Box>

          <Grid container spacing={2} px={4} mt={2}>
            <Grid item xs={12}>
              <Typography variant="h6" color={"white"}>
                Data
              </Typography>
              <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                <CustomTable
                  rowsPerPage={tableData.length}
                  rows={tableData}
                  headers={
                    keyType === "planets" ? tableHeadersNew : tableHeaders
                  }
                  isSelected={(id: any) => selected.includes(id)}
                  handleClick={setSelectedData}
                  checkboxSelection
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography variant="h6" color={"white"}>
                Selected - Planets
              </Typography>
              <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                <CustomTable
                  rowsPerPage={selectedPlanetsRows.length}
                  rows={selectedPlanetsRows}
                  headers={tableHeadersAllOptions}
                  handleClick={(e: any, row: any) => deleteRow(row)}
                  checkboxSelection={false}
                  deleteIcon
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography variant="h6" color={"white"}>
                Selected - Other
              </Typography>
              <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                <CustomTable
                  rowsPerPage={
                    persistedSelectedData.filter((r) => r.pdf_type === "other")
                      .length
                  }
                  rows={persistedSelectedData.filter(
                    (r) => r.pdf_type === "other",
                  )}
                  headers={tableHeaders}
                  handleClick={(e: any, row: any) => deleteRow(row)}
                  checkboxSelection={false}
                  deleteIcon
                />
              </Box>
            </Grid>
          </Grid>
        </>
      )}
      {!isDataTab &&
        (rahuDetails.d1Rahu ||
          rahuDetails.d1RahuBhava ||
          rahuDetails.d10Rahu ||
          rahuDetails.d10RahuBhava) && (
          <Box px={4} mt={2}>
            <Grid container spacing={3}>
              {(rahuDetails.d1Rahu || rahuDetails.d1RahuBhava) && (
                <Grid item xs={12} md={6}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel
                      component="legend"
                      sx={{ color: "white", mb: 1 }}
                    >
                      D1 Rahu/Ketu
                    </FormLabel>
                    <RadioGroup
                      value={d1Rashi ? "d1Rahu" : "d1RahuBhava"}
                      onChange={(_, v) => setD1Rashi(v === "d1Rahu")}
                      sx={{ gap: 1 }}
                    >
                      {rahuDetails.d1Rahu && (
                        <Box
                          sx={
                            d1Rashi
                              ? {
                                  ...classes.priceRadioBox,
                                  ...classes.selectedPriceRadioBox,
                                }
                              : classes.priceRadioBox
                          }
                          onClick={() => setD1Rashi(true)}
                        >
                          <Radio
                            checked={d1Rashi}
                            value="d1Rahu"
                            onChange={() => setD1Rashi(true)}
                            sx={classes.priceRadioButton}
                          />
                          <Stack direction="column">
                            <Typography sx={classes.planText}>
                              {rahuDetails.d1Rahu.name}
                            </Typography>
                            {Array.isArray(rahuDetails.d1Rahu.value) &&
                              rahuDetails.d1Rahu.value.length > 0 && (
                                <Typography
                                  sx={{ ...classes.planDescText, mt: 0.5 }}
                                >
                                  {rahuDetails.d1Rahu.value.join(" - ")}
                                </Typography>
                              )}
                          </Stack>
                        </Box>
                      )}
                      {rahuDetails.d1RahuBhava && (
                        <Box
                          sx={
                            !d1Rashi
                              ? {
                                  ...classes.priceRadioBox,
                                  ...classes.selectedPriceRadioBox,
                                }
                              : classes.priceRadioBox
                          }
                          onClick={() => setD1Rashi(false)}
                        >
                          <Radio
                            checked={!d1Rashi}
                            value="d1RahuBhava"
                            onChange={() => setD1Rashi(false)}
                            sx={classes.priceRadioButton}
                          />
                          <Stack direction="column">
                            <Typography sx={classes.planText}>
                              {rahuDetails.d1RahuBhava.name}
                            </Typography>
                            {Array.isArray(rahuDetails.d1RahuBhava.value) &&
                              rahuDetails.d1RahuBhava.value.length > 0 && (
                                <Typography
                                  sx={{ ...classes.planDescText, mt: 0.5 }}
                                >
                                  {rahuDetails.d1RahuBhava.value.join(" - ")}
                                </Typography>
                              )}
                          </Stack>
                        </Box>
                      )}
                    </RadioGroup>
                  </FormControl>
                </Grid>
              )}
              {(rahuDetails.d10Rahu || rahuDetails.d10RahuBhava) && (
                <Grid item xs={12} md={6}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel
                      component="legend"
                      sx={{ color: "white", mb: 1 }}
                    >
                      D10 Rahu/Ketu
                    </FormLabel>
                    <RadioGroup
                      value={d10Rashi ? "d10Rahu" : "d10RahuBhava"}
                      onChange={(_, v) => setD10Rashi(v === "d10Rahu")}
                      sx={{ gap: 1 }}
                    >
                      {rahuDetails.d10Rahu && (
                        <Box
                          sx={
                            d10Rashi
                              ? {
                                  ...classes.priceRadioBox,
                                  ...classes.selectedPriceRadioBox,
                                }
                              : classes.priceRadioBox
                          }
                          onClick={() => setD10Rashi(true)}
                        >
                          <Radio
                            checked={d10Rashi}
                            value="d10Rahu"
                            onChange={() => setD10Rashi(true)}
                            sx={classes.priceRadioButton}
                          />
                          <Stack direction="column">
                            <Typography sx={classes.planText}>
                              {rahuDetails.d10Rahu.name}
                            </Typography>
                            {Array.isArray(rahuDetails.d10Rahu.value) &&
                              rahuDetails.d10Rahu.value.length > 0 && (
                                <Typography
                                  sx={{ ...classes.planDescText, mt: 0.5 }}
                                >
                                  {rahuDetails.d10Rahu.value.join(" - ")}
                                </Typography>
                              )}
                          </Stack>
                        </Box>
                      )}
                      {rahuDetails.d10RahuBhava && (
                        <Box
                          sx={
                            !d10Rashi
                              ? {
                                  ...classes.priceRadioBox,
                                  ...classes.selectedPriceRadioBox,
                                }
                              : classes.priceRadioBox
                          }
                          onClick={() => setD10Rashi(false)}
                        >
                          <Radio
                            checked={!d10Rashi}
                            value="d10RahuBhava"
                            onChange={() => setD10Rashi(false)}
                            sx={classes.priceRadioButton}
                          />
                          <Stack direction="column">
                            <Typography sx={classes.planText}>
                              {rahuDetails.d10RahuBhava.name}
                            </Typography>
                            {Array.isArray(rahuDetails.d10RahuBhava.value) &&
                              rahuDetails.d10RahuBhava.value.length > 0 && (
                                <Typography
                                  sx={{ ...classes.planDescText, mt: 0.5 }}
                                >
                                  {rahuDetails.d10RahuBhava.value.join(" - ")}
                                </Typography>
                              )}
                          </Stack>
                        </Box>
                      )}
                    </RadioGroup>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      <Stack direction="row" spacing={2} mt={4} px={4}>
        <CustomButton
          label={t("generateFullReport")}
          onClick={fetchReportByProfileId}
          type={strings.PRIMARY}
          customClasses={{ fontSize: "16px" }}
        />
        <CustomButton
          label={"Book-1 + Book-2"}
          onClick={() => fetchReportByProfileId(1)}
          type={strings.PRIMARY}
        />
        <CustomButton
          label={t("generateShortResume")}
          onClick={fetchShortReport}
          type={strings.PRIMARY}
          customClasses={{ fontSize: "16px" }}
        />
      </Stack>

      <Modal open={isOpen} onClose={() => setIsOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            height: "90%",
            bgcolor: "background.paper",
            p: 2,
            overflowY: "auto",
            borderRadius: "8px",
          }}
        >
          <IconButton
            onClick={() => setIsOpen(false)}
            sx={{ position: "absolute", top: 8, right: 8, zIndex: 1000 }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ height: "100%" }}>
            <HtmlEditor
              content={content}
              onSave={handleContentSave}
              fileName={
                formFields.firstName.value.trim() +
                " " +
                formFields.lastName.value.trim()
              }
              onClose={() => setIsOpen(false)}
            />
          </Box>
        </Box>
      </Modal>

      <Modal
        open={selectorModalOpen}
        onClose={() => {
          setSelectorModalOpen(false);
          setRowPendingSelection(null);
          setModalSelectedOptions([]);
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 300,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" mb={2}>
            Select value(s) for this row
          </Typography>
          <Stack spacing={2}>
            {selectorOptions.map((opt) => (
              <Box key={opt.value} display="flex" alignItems="center">
                <input
                  type="checkbox"
                  id={`modal-opt-${opt.value}`}
                  checked={modalSelectedOptions.includes(opt.value)}
                  onChange={() => handleModalOptionToggle(opt.value)}
                />
                <label
                  htmlFor={`modal-opt-${opt.value}`}
                  style={{ marginLeft: 8 }}
                >
                  {opt.label}
                </label>
              </Box>
            ))}
            <CustomButton
              label="Confirm"
              onClick={handleModalConfirm}
              type={strings.PRIMARY}
              disabled={modalSelectedOptions.length === 0}
            />
          </Stack>
        </Box>
      </Modal>
    </>
  );
};

export default Data;
